import "apps/pathways/styles/components/survey.scss";
import React from 'react';
import PropTypes from 'prop-types';
import { Route, Redirect, Link } from 'library/util/core-router';
import PredictableShuffle from 'library/util/predictable-shuffle';
import ScrollFixed from 'library/components/scroll-fixed';
import ProgressBar from 'library/components/progress';
import Cache from 'library/util/cache';
import Request from 'apps/pathways/util/survey/ajax';
import { resolve, set } from 'library/helpers/path-resolver';
import SavingButton from 'library/components/saving-button';
import Scroll from 'library/util/scroll';
import Loading from 'library/components/loading';
import Versions from 'apps/pathways/components/survey/versions';
import Modal from 'library/helpers/modal';
import Notification, { Error as ErrorNotification } from 'library/components/notification';

import RealInstructionsModal from 'apps/pathways/components/survey/instructions/real';
import IdealInstructionsModal from 'apps/pathways/components/survey/instructions/ideal';

const INSTRUCTIONS_MAP = {
  'ideal': IdealInstructionsModal,
  'real': RealInstructionsModal
};

const Wait = (timeout) => new Promise((resolve) => {
  setTimeout(resolve, timeout);
});

export default class Survey extends React.Component {
    static defaultProps = {
      mode: 'real',
      topThreshold: 200
    }

    constructor(...params) {
      super(...params);
    }

    state = {
      progress: 0,
      loading: false,
      adjectives: [],
      responses: {},
      mode: 'real',
      version: 'list',
      instructions: null,
      saving: false,
      hasBeenInView: false
    }

    componentWillMount() {
      this.loadAdjectives();

    }

    componentDidMount() {
      this.modal = new Modal('survey-instructions');
      window.addEventListener('scroll', this.checkInView);
      this.checkInView();
    }

    checkInView = () => {
      if (this.surveyContainer && !this.state.hasBeenInView) {
        const isInView = (
          this.surveyContainer.getBoundingClientRect().top < this.props.topThreshold
          || this.surveyContainer.getBoundingClientRect().bottom < (window.innerHeight + this.props.topThreshold)
        );
        if (isInView) {
          this.setState({
            hasBeenInView: true
          });
          window.removeEventListener('scroll', this.checkInView);
        }
      }
    }

    componentWillUnmount() {
      if (this.modal && this.modal.isRendered) {
        this.modal.unmount();
      }
      window.removeEventListener('scroll', this.checkInView);
    }

    componentDidUpdate(prevProps) {
      const { instructions, mode, saving, hasBeenInView } = this.state;
      if (instructions) {
        if (hasBeenInView) {
          this.modal.render(INSTRUCTIONS_MAP[mode] || null, {
            onComplete: this.handleComplete,
            saving: saving
          });
        }
      } else if (this.modal && this.modal.isRendered) {
        this.modal.unmount();
      }

      if (mode === 'complete') {
        this.props.onComplete();
      }
    }

    toggleActive = (id, mode) => {
      let state = {...this.state};
      state = set(this.state, `responses.${id}.${mode}`, !resolve(this.state, `responses.${id}.${mode}`, false, '.'), true, '.');
      this.setState(state, this.updateCache);
    }

    savingResponses = false;

    get responses() {
      const { responses, adjectives } = this.state;
      return adjectives.map(({ id }) => ({
        id: id,
        real: resolve(responses, `${id}.real`, false, '.'),
        ideal: resolve(responses, `${id}.ideal`, false, '.')
      }));
    }

    saveResponses = async (responses, adjectives) => {
      const { token, mode } = this.props;
      this.savingResponses = true;

      await Request.ajax.post(`/api/assessment/assessments/${token}/responses`, {
        responses: this.responses
      });

      this.savingResponses = false;
    }

    updateCache = async () => {
      await Cache.set(this.cache_token, this.state.responses, __APPNAME__);

      if (!this.savingResponses) {
        await this.saveResponses();
      }
    }

    get cache_token () {
      const { token } = this.props;
      return `survey_${token}`;
    }

    loadAdjectives = async () => {
      const { token, user } = this.props;
      this.setState({
        loading: true
      });

      let adjectives = [];
      try {
        const { data } = await Request.ajax(`/api/assessment/adjectives?langs=${user.preferred_language||'simplified-english'}`);
        adjectives = PredictableShuffle(token, data.adjectives);

        let responses = await Cache.get(this.cache_token, __APPNAME__);
        if (!responses) {
          const { data:responseData } = await Request.ajax(`/api/assessment/assessments/${token}/responses`);
          responses = responseData.responses;
        }

        const { data:{assessment} } = await Request.ajax(`/api/assessment/assessments/${token}`);
        const [mode,instructions] = (assessment.state || '').split('_');

        this.setState({
          mode: mode,
          version: assessment.version,
          instructions: !!instructions,
          responses: responses || {}
        });

      } catch (e) {
        this.props.onError(e);
      }

      this.setState({
        adjectives: adjectives,
        loading: false
      });
    }

    handleProgress = (percent) => {
      this.setState({
        progress: percent
      });
    }

    handleComplete = async (nextState) => {
      const { mode, instructions } = this.state;
      const { token } = this.props;

      const [ nextMode, nextInstructions ] = nextState.split('_');

      this.setState({
        saving: true,
        mode: nextMode,
        instructions: !!nextInstructions
      });

      if (mode === 'real' || mode === 'real_instructions') {
        this.setState((nextState) => {
          nextState = {...nextState};
          for (var key in nextState.responses) {
            nextState.responses[key].ideal = nextState.responses[key].real;
          }
          return nextState;
        });
      }

      try {
        await this.saveResponses();

        const { data:{assessment} } = await Request.ajax.put(`/api/assessment/assessments/${token}`, {
          assessment: {
            state: nextState
          }
        });

        const [newMode,newInstructions] = (assessment.state || '').split('_');

        this.setState({
          mode: newMode,
          instructions: !!newInstructions
        });

        if (newMode === 'complete') {
          this.props.onComplete();
        }

      } catch (e) {
        console.error(e);
        this.setState({
          mode: mode,
          instructions: instructions
        });
        Notification.new(
          {timeout: 5000},
          <ErrorNotification>
            Error saving!
          </ErrorNotification>
        );
      }

      this.setState({
        saving: false
      });
    }

    setMode = async (mode) => {
      const { token } = this.props;
      const { data:{assessment} } = await Request.ajax.put(`/api/assessment/assessments/${token}`, {
        assessment: {
          state: mode
        }
      });
      const [newMode,instructions] = (assessment.state || '').split('_');

      this.setState({
        mode: newMode,
        instructions: !!instructions
      });
    }

    render() {
      const {
        adjectives,
        loading,
        mode,
        responses,
        saving,
        version
      } = this.state;

      const {
        user
      } = this.props;

      if (loading) {
        return (
          <div className="survey-loading">
            <h2>
              HANG TIGHT!
              <div><small>We are preparing your survey</small></div>
            </h2>
            <Loading />
          </div>
        );
      }

      const Component = Versions[version] || Versions.list;

      return (
        <div ref={r=>this.surveyContainer=r} className="survey">
          <ScrollFixed>
            <ProgressBar progress={this.state.progress} className="progress" />
          </ScrollFixed>
          <Component preferred_language={user.preferred_language||'simplified-english'} saving={saving} onComplete={this.handleComplete} adjectives={adjectives} responses={responses} mode={mode} toggleActive={this.toggleActive} onProgress={this.handleProgress} />
        </div>
      );
    }
}
